import axios from "./_axios";
import { API } from "./apiEndPoints";
import { refreshAccountCounts } from "@/utils/accountCountUtils";

/** Query-string serializer for role list endpoints (arrays → repeated keys). */
function serializeRoleAttachmentListParams(params) {
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      value.forEach((v) => {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
      });
    } else if (value !== undefined && value !== "") {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });
  return parts.join("&");
}

/** Legacy serializer for org role / responsibility list (only omits `undefined`, matches previous inline implementations). */
function serializeRoleListQueryParamsLoose(params) {
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      value.forEach((v) => {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
      });
    } else if (value !== undefined) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });
  return parts.join("&");
}

function applyRoleAttachmentListFilters(params, filters = {}) {
  if (filters.status && filters.status.length > 0) {
    params.status = filters.status;
  }
  if (filters.no_masters) {
    params.no_masters = true;
  }
  if (filters.no_coaches) {
    params.no_coaches = true;
  }
  if (filters.no_professionals) {
    params.no_professionals = true;
  }
  if (filters.archived !== undefined) {
    params.archived = filters.archived;
  }
  if (filters.positions === "no") {
    params.positions = "no";
  }
  if (filters.responsibilities === "no") {
    params.responsibilities = "no";
  }
  // ER-10902: validity (Invalid) and in-use (Not used) filters for the Roles List
  // indicator row. The backend accepts these once ER-10903 lands; django-filter
  // ignores unknown params meanwhile, so flag-gated clicks are safe today.
  if (filters.is_invalid !== undefined) {
    params.is_invalid = filters.is_invalid;
  }
  if (filters.in_use !== undefined) {
    params.in_use = filters.in_use;
  }
  if (filters.recently_activated) {
    params.recently_activated = true;
  }
  if (filters.recently_retired) {
    params.recently_retired = true;
  }
  if (filters.recently_deactivated) {
    params.recently_deactivated = true;
  }
  if (filters.recently_created) {
    params.recently_created = true;
  }
  return params;
}

/**
 * Roles attached to a position or responsibility (shared filter/search/pagination).
 * @param {"position"|"responsibility"} scopeKey
 */
function getListOfRolesAttachedToPositionOrResponsibility(
  account_id,
  limit,
  offset,
  search,
  filters,
  scopeKey,
  scopeId
) {
  const params = applyRoleAttachmentListFilters(
    {
      limit,
      offset,
      name: search,
      [scopeKey]: scopeId,
    },
    filters
  );
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/`,
    params,
    paramsSerializer: serializeRoleAttachmentListParams,
  });
}

//https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_role_list
export const getListOfRoles = async (account_id, page_no, size) => {
  let result = await axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/`,
    params: { page: page_no, size: size },
  });
  return result;
};
export const getListOfRolesV2 = (
  account_id,
  limit,
  offset,
  search,
  filters = {},
  ordering = null
) => {
  const params = applyRoleAttachmentListFilters({ limit, offset, name: search }, filters);
  if (ordering) {
    params.ordering = ordering;
  }
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/`,
    params,
    paramsSerializer: serializeRoleListQueryParamsLoose,
  });
};
export const getRolesAssignedToPosition = (account_id, positionId) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/?position=${positionId}`,
  });
  return result;
};

/** Role-to-position attachment rows for a position (authoritative attach list + order). */
export const getRoleAttachmentsForPosition = (account_id, position_id) => {
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/position/${position_id}/role/`,
  });
};
export const getRolesListAvailableForPosition = (account_id, positionId, limit, offset, search) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/`,
    params: {
      attachable_to: positionId,
      limit: limit,
      offset: offset,
      name: search,
    },
  });
  return result;
};

// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_role_read
export const getRole = async (account_id, role_id) => {
  let result = await axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/`,
    params: {},
  });
  return result;
};
// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_role_create
export const addRole = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/`,
    data: data,
  });
  return result;
};
// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_role_update
export const updateRole = (account_id, role_id, data) => {
  let result = axios()({
    method: "put",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/`,
    data: data,
  });
  return result;
};
// PATCH method for partial updates to role
export const editRole = (account_id, role_id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/`,
    data: data,
  });
  return result;
};
/**
 * Create a DRAFT Role and attach it to a Position in one atomic operation (ER-12095 / MDP-617).
 * Gated by idea-MDP-617 flag (404 when off).
 * @param {number|string} account_id
 * @param {object} data - { name, position_id, context? }
 * @returns {Promise} Axios response with created Role
 */
export const createDraftAndAttachToPosition = (account_id, data) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/create-draft-and-attach-to-position/`,
    data: data,
  });
};
// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_attach_create
export const attachResponsibilityToRole = (account_id, roleId, data) => {
  const resId = data?.attachment_id;
  const payload = {
    role: roleId,
    responsibility: resId,
  };
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${resId}/role-to-responsibility/`,
    data: payload,
  });
  return result;
};
export const updateActiveRole = async (account_id, role_id, data) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/activate/`,
    data: data,
  });
  await refreshAccountCounts(account_id);
  return result;
};

// ER-11818 / Roles Rule 3.4 — read-only preview of how many employees will be assigned
// when a DRAFT Role is activated (N). Contract owned by backend ER-11822.
export const getRoleActivatePreview = (account_id, role_id) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/activate/preview/`,
  });
  return result;
};
// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_role_update
export const updateRetiredRole = async (account_id, role_id, data) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/retire/`,
    data: data,
  });
  await refreshAccountCounts(account_id);
  return result;
};
export const updateArchiveRole = async (account_id, objType, orgId) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/${objType}/${orgId}/archive/`,
  });
  await refreshAccountCounts(account_id);
  return result;
};
export const updateUnArchiveRole = async (account_id, objType, orgId, name) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/${objType}/${orgId}/unarchive/`,
    ...(name ? { data: { name } } : {}),
  });
  await refreshAccountCounts(account_id);
  return result;
};
// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_role_delete
export const deleteReadinessRole = (account_id, role_id) => {
  let result = axios()({
    method: "delete",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/`,
  });
  return result;
};
export const deleteReadinessRoleWithResponsibilities = (account_id, role_id, data) => {
  let result = axios()({
    method: "delete",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/?delete_draft_responsibilities=${data.delete_draft_responsibilities}`,
  });
  return result;
};
// https://api.dev.exiqtive.com/api/common/account/{account_id}/{type}/{obj_id}/de-activate/
export const updateDeActiveRole = async (account_id, role_id, data) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/de-activate/`,
    data: data,
  });
  await refreshAccountCounts(account_id);
  return result;
};
// https://api.dev.exiqtive.com/api/common/account/{account_id}/{type}/{obj_id}/re-activate/
export const updateReActiveRole = async (account_id, role_id, data) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/re-activate/`,
    data: data,
  });
  await refreshAccountCounts(account_id);
  return result;
};

// ER-11818 / Roles Rule 3.6 — read-only preview of how many employees will be assigned
// when a RETIRED Role is reactivated (N). Contract owned by backend ER-11822.
export const getRoleReactivatePreview = (account_id, role_id) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/re-activate/preview/`,
  });
  return result;
};
//https://api.dev.exiqtive.com/api/common/account/{account_id}/position/{id}/
export const getPositionId = (account_id, pos_id) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/position/${pos_id}/`,
  });
  return result;
};
//https://api.dev.exiqtive.com/redoc/#tag/proficiency/operation/Get%20role%20chatter
//https://api.dev.exiqtive.com/api/proficiency/account/{account_id}/role/{role_id}/chatter/
export const getRoleChatter = (account_id, role_id, page = 1) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/role/${role_id}/chatter/`,
    params: { page: page },
  });
  return result;
};

export const getRoleId = (account_id, role_id) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/`,
  });
  return result;
};

export const detachAttachments = (account_id, role_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/responsibility/${role_id}/detach/`,
    data: data,
  });
  return result;
};

export const detachRoleAttachment = (account_id, obj_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${obj_id}/detach/`,
    data: data,
  });
  return result;
};

export const detachOrgObject = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/${data.childType}/${data.childId}/detach/`,
    data: data.parentData,
  });
  return result;
};

export const updateNameRole = (account_id, role_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/${role_id}/rename/`,
    data: data,
  });
  return result;
};

export const attachResponsibilitiesToRoleBulk = (account_id, role_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.PROFICIENCY_V1}${account_id}/role/${role_id}/responsibility-to-role-bulk/`,
    data: data,
  });
  return result;
};

export const getRoleAssignment = (
  account_id,
  employee_id,
  position_assignment_id,
  status = null
) => {
  let params = `employee=${employee_id}&position_assignment=${position_assignment_id}`;
  if (status) {
    params += `&status=${status}`;
  }

  let result = axios()({
    method: "get",
    url: `${API.PROFICIENCY_V2}${account_id}/role-assignment/?${params}`,
  });
  return result;
};

export const getRoleAttachmentsFromResponsibilities = (account_id, role_id) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/`,
    params: { role: role_id, limit: 50 },
  });
  return result;
};

export const getRoleAttachmentsFromPositions = (account_id, role_id) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/position/`,
    params: { role: role_id, limit: 50 },
  });
  return result;
};

/**
 * List positions attached to a role (for AttachedToPositionsTab).
 * Supports server-side search, filters, and pagination (project standard).
 * @param {number|string} account_id
 * @param {number|string} roleId
 * @param {number} limit
 * @param {number} offset
 * @param {string} search - name search
 * @param {object} filters - { status: string[], archived: boolean }
 * @returns {Promise} Axios response with data.results and data.count
 */
export const getListOfPositionsAttachedToRole = (
  account_id,
  roleId,
  limit,
  offset,
  search = "",
  filters = {}
) => {
  const params = {
    role: roleId,
    limit,
    offset,
    name: search || undefined,
  };
  if (filters.status && filters.status.length > 0) {
    params.status = filters.status;
  }
  if (filters.archived !== undefined && filters.archived !== null) {
    params.archived = Boolean(filters.archived);
  }
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/position/`,
    params,
    paramsSerializer: (params) => {
      const parts = [];
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (Array.isArray(value)) {
          value.forEach((v) => {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
          });
        } else if (value !== undefined && value !== "") {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      });
      return parts.join("&");
    },
  });
};

export const getListOfRolesAttachedToPosition = (
  account_id,
  limit,
  offset,
  search,
  positionId,
  filters = {}
) =>
  getListOfRolesAttachedToPositionOrResponsibility(
    account_id,
    limit,
    offset,
    search,
    filters,
    "position",
    positionId
  );

/** Roles attached to a given responsibility (same filters/search shape as {@link getListOfRolesAttachedToPosition}). */
export const getListOfRolesAttachedToResponsibility = (
  account_id,
  limit,
  offset,
  search,
  responsibilityId,
  filters = {}
) =>
  getListOfRolesAttachedToPositionOrResponsibility(
    account_id,
    limit,
    offset,
    search,
    filters,
    "responsibility",
    responsibilityId
  );

export const getListOfResponsibilitiesAttachedToRole = (
  account_id,
  limit,
  offset,
  search,
  roleId,
  filters = {},
  include_badge_holders = false
) => {
  const params = applyRoleAttachmentListFilters(
    {
      limit,
      offset,
      name: search,
      role: roleId,
      include_badge_holders,
    },
    filters
  );
  return axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/`,
    params,
    paramsSerializer: serializeRoleListQueryParamsLoose,
  });
};

/** PATCH reorder responsibilities on a role. Body: `{ responsibility_order: number[] }`. */
export const reorderRoleResponsibilities = (account_id, role_id, responsibility_order) => {
  return axios()({
    method: "patch",
    url: `${API.PROFICIENCY_V2}${account_id}/role/${role_id}/reorder-responsibilities`,
    data: { responsibility_order },
  });
};
